import { InjectionToken } from '@angular/core';

export const COUNTERPARTY_ADMINISTRATION_API_KEY = new InjectionToken<string>('CounterpartyAdministrationApiKey');

/**
 * UUID generated to guarantee unicity for both single and bulk IBAN-name verification requests.
 */
export type VopReference = string;

/**
 * Business Identifier Code, an international standard for identification of institutions within the financial services industry, described by standard ISO 9362.
 */
export type Bic = string;

/**
 * Not used in single party verification, mandatory in bulk verification.
 * Unique End-to-End Transaction Reference is an RFC4122 UUID used as a record-level correlation ID to uniquely track and trace individual transactions throughout the process.
 */
export type Uetr = string;

/**
 * The unique identifier of the creditor.
 */
export type CreditorId = string;

export interface PartyAccount {
    iban: string;
    name: string;
}

export interface Iban {
    iban: string;
}

export interface SingleVerificationResponse extends VerificationResult {
    vop_reference: VopReference;
}

export interface VerificationResult {
    uetr?: Uetr;
    match_result: MatchResult;
    matched_name?: string;
    matched_name_legacy?: string;
    account_holder_type?: AccountHolderType;
    detailed_match_result?: DetailedMatchResult;
    account_status?: AccountStatus;
    town_name?: string;
    town_name_legacy?: string;
    switching_information?: SwitchingInformation;
}

export interface SwitchingInformation {
    switching_service_for_account?: SwitchingServiceForAccount;
    switched_to_iban?: string;
}

export interface AddressbookItems {
    addressbook_items: AddressbookItem[];
}

export interface AddressbookItem {
    id?: string;
    name?: string;
    account_number?: string;
    assigned_name?: string;
    last_used_date?: string;
    creditor_address?: string;
    creditor_city?: string;
    creditor_country_code?: string;
    bank_bic?: string;
    bank_code?: string;
    bank_name?: string;
    bank_address?: string;
    bank_city?: string;
    bank_country_code?: string;
}

export interface BicResponse {
    bic?: string;
}

export interface BankAddress {
    street_name?: string;
    building_number?: string;
    post_code?: string;
    town_name?: string;
    country_subdivision_name?: string;
    country_code?: string;
    country_name?: string;
}

export interface BicDetailsResponse {
    bic?: string;
    name?: string;
    office_type?: string;
    department?: string;
    address?: BankAddress;
    utc_offset?: string;
}

// Enums

/**
 * Contains the result of the IBAN name match validation.
 */
export enum MatchResult {
    MATCH = 'MATCH',
    CLOSE_MATCH = 'CLOSE_MATCH',
    NO_MATCH = 'NO_MATCH',
    NOT_APPLICABLE = 'NOT_APPLICABLE',
    UNAVAILABLE = 'UNAVAILABLE',
    UNKNOWN = 'UNKNOWN',
    IBAN_INVALID = 'IBAN_INVALID'
}

/**
 * Describes the type of the account holder which can be a Natural Person, an Organisation or Unknown.
 */
export enum AccountHolderType {
    NP = 'NP',
    ORG = 'ORG',
    UNKNOWN = 'UNKNOWN'
}

/**
 * Additional information in case the matching result was a 'NO_MATCH' or 'NOT_APPLICABLE'.
 */
export enum DetailedMatchResult {
    NAME_TOO_SHORT = 'NAME_TOO_SHORT',
    BANK_NOT_FOUND = 'BANK_NOT_FOUND',
    OPTED_OUT = 'OPTED_OUT',
    ID_NOT_SUPPORTED = 'ID_NOT_SUPPORTED',
    NOT_FOUND = 'NOT_FOUND'
}

/**
 * The status of the account
 */
export enum AccountStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    UNKNOWN = 'UNKNOWN'
}

/**
 * The status of the account that switched to another bank.
 * Indicates if the current IBAN is still active or not.
 */
export enum SwitchingServiceForAccount {
    ACTIVE = 'ACTIVE',
    EXPIRED = 'EXPIRED'
}
